// src/rms/utils/rmsApi.js
/**
 * RMS client → Cloud Functions API wrappers (Gen2, region europe-west2).
 * What:
 *   Typed, minimal wrappers around httpsCallable endpoints used by the RMS wizard.
 * Why:
 *   Centralize function names, payload shapes and return types; keep UI lean.
 * Security:
 *   - Endpoints are public invoker at Cloud Run layer but enforce auth/validation server‑side.
 *   - Draft operations require a valid clientToken (opaque UUID; server stores only its hash).
 * Env:
 *   - Functions are initialized in firebase.js with region 'europe-west2'.
 */
import { functions } from '../../firebase';
import { httpsCallable } from 'firebase/functions';

/**
 * Fetch a form schema (by live pointer or explicit version).
 * @param {{orgId: string, formKey: string, version?: string}} args
 * @returns {Promise<{schema: object, version: string}>}
 * @throws FirebaseError on transport or callable HttpsError (e.g., NOT_FOUND)
 */
export async function apiGetFormSchema({ orgId, formKey, version }) {
  const fn = httpsCallable(functions, 'rmsGetFormSchema');
  const res = await fn({ orgId, formKey, version });
  return res.data; // { schema, version }
}

/**
 * Create a new referral prospect and an empty form instance.
 * @param {{orgId: string, formKey: string, idempotencyKey: string}} args
 * @returns {Promise<{prospectId: string, clientToken: string, schemaVersion: string}>}
 */
export async function apiCreateIntent({ orgId, formKey, idempotencyKey, initiatedBy }) {
  const fn = httpsCallable(functions, 'rmsCreateIntent');
  const res = await fn({ orgId, formKey, idempotencyKey, initiatedBy });
  return res.data; // { prospectId, clientToken, schemaVersion }
}

/**
 * Save a partial step patch into the draft.
 * @param {{
 *   orgId: string,
 *   prospectId: string,
 *   clientToken: string,
 *   formKey: string,
 *   patch: Record<string, unknown>,
 *   stepId?: string
 * }} args
 * @returns {Promise<{ok: boolean, updatedAt: string, progress: { totalQuestions: number, answeredQuestions: number, percent: number }}>}
 */
export async function apiSaveIntentDraft({ orgId, prospectId, clientToken, formKey, patch, stepId }) {
  const fn = httpsCallable(functions, 'rmsSaveIntentDraft');
  const res = await fn({ orgId, prospectId, clientToken, formKey, patch, stepId });
  return res.data; // { ok, updatedAt, progress }
}

/**
 * Submit the draft for validation and eligibility decision.
 * Idempotent when idempotencyKey is provided.
 * @param {{
 *   orgId: string,
 *   prospectId: string,
 *   clientToken: string,
 *   formKey: string,
 *   idempotencyKey?: string
 * }} args
 * @returns {Promise<{decision: 'eligible'|'ineligible', reasonCode?: string, usedPostcodeType?: string, serviceAreaVersion?: string, inviteId?: string, previewUrl?: string, idempotent?: boolean}>}
 */
export async function apiSubmitIntent({ orgId, prospectId, clientToken, formKey, idempotencyKey }) {
  const fn = httpsCallable(functions, 'rmsSubmitIntent');
  const res = await fn({ orgId, prospectId, clientToken, formKey, idempotencyKey });
  return res.data; // { decision, reasonCode?, usedPostcodeType, serviceAreaVersion }
}

/**
 * Check if an email address is already in use by an existing account.
 * Used during pre-account referral initiation to detect if a user should sign in
 * instead of creating a new referral.
 * @param {{orgId: string, email: string}} args
 * @returns {Promise<{available: boolean, hasAccount: boolean}>}
 */
export async function apiCheckEmailAvailable({ orgId, email }) {
  const fn = httpsCallable(functions, 'rmsCheckEmailAvailable');
  const res = await fn({ orgId, email });
  return res.data; // { available, hasAccount }
}

/**
 * Load current draft state for resume/refresh flows.
 * @param {{orgId: string, prospectId: string, clientToken: string, formKey: string}} args
 * @returns {Promise<{answers: object, progress: object, schemaVersion: string, lastStepId?: string}>}
 */
export async function apiGetIntentState({ orgId, prospectId, clientToken, formKey }) {
  const fn = httpsCallable(functions, 'rmsGetIntentState');
  const res = await fn({ orgId, prospectId, clientToken, formKey });
  return res.data; // { answers, progress, schemaVersion, lastStepId }
}

/**
 * Rotate/refresh the opaque client token for the draft (extends TTL).
 * @param {{orgId: string, prospectId: string, clientToken: string}} args
 * @returns {Promise<{clientToken: string, expIso: string}>}
 */
export async function apiRefreshIntentToken({ orgId, prospectId, clientToken }) {
  const fn = httpsCallable(functions, 'rmsRefreshIntentToken');
  const res = await fn({ orgId, prospectId, clientToken });
  return res.data; // { clientToken, expIso }
}

/**
 * Complete initial referrer account setup (school or guardian).
 * Server validates the invite ↔ prospect linkage and finalizes account + membership + patient record.
 * Prefer this over apiCompleteInvitation going forward; this consolidates both referrer roles.
 * @param {{ orgId: string, intentId: string, inviteId?: string }} args
 * @returns {Promise<{ userId: string, membershipId?: string, patientRecordId?: string, role?: 'school_user'|'guardian', status?: string }>}
 */
export async function apiCompleteReferrerAccountSetup({ orgId, intentId, inviteId }) {
  const fn = httpsCallable(functions, 'rmsCompleteReferrerAccountSetup');
  const res = await fn({ orgId, intentId, inviteId });
  return res.data;
}

/**
 * Finalise an invited RMS provider's onboarding (status invited -> active,
 * invite consumed, hasCompletedSetup claim granted). The backend verifies an
 * enrolled second factor on the Auth record before activating, so calling
 * this without MFA enrolled rejects with failed-precondition. Idempotent for
 * already-active providers.
 * @param {{ orgId: string }} args
 * @returns {Promise<{ok: boolean}>}
 */
export async function apiCompleteProviderSetup({ orgId }) {
  const fn = httpsCallable(functions, 'rmsCompleteProviderSetup');
  const res = await fn({ orgId });
  return res.data; // { ok: true }
}

/**
 * Send a passwordless sign-in link via backend (Admin SDK + SendGrid template).
 * @param {{ orgId: string, email: string, continuePath?: string }} args
 * @returns {Promise<{ok: boolean}>}
 */
export async function apiSendPasswordlessSignInLink({ orgId, email, continuePath = '/rms/welcome' }) {
  const fn = httpsCallable(functions, 'rmsSendPasswordlessSignInLink');
  const res = await fn({ orgId, email, continuePath });
  return res.data; // { ok: true }
}

/**
 * Swap an emailed sign-in link's one-time token for a Firebase custom token
 * (TDI-706). The server refuses a missing, wrong, used or expired token with
 * one NOT_FOUND error.
 * @param {{ orgId: string, token: string }} args
 * @returns {Promise<{customToken: string, continuePath: string}>}
 */
export async function apiRedeemSignInLink({ orgId, token }) {
  const fn = httpsCallable(functions, 'rmsRedeemSignInLink');
  const res = await fn({ orgId, token });
  return res.data;
}

/**
 * Ask for a password reset email for a school account (SENCO or class
 * teacher). The server decides what, if anything, is sent and always answers
 * the same, so nothing here reveals whether the address has an account.
 * @param {{email: string}} args
 * @returns {Promise<{ok: true}>}
 */
export async function apiSendSchoolUserPasswordReset({ email }) {
  const fn = httpsCallable(functions, 'rmsSendSchoolUserPasswordReset');
  const res = await fn({ email });
  return res.data;
}

/**
 * POST-ACCOUNT FORMS (formInstances under patientRecords)
 * These endpoints mirror pre-account intent endpoints but are scoped to a patientRecordId and require auth.
 */

/**
 * Create a new form instance for a patient record.
 * @param {{ orgId: string, patientRecordId: string, formKey: string }} args
 * @returns {Promise<{ instanceId: string, schemaVersion: string, context?: object }>}
 */
export async function apiCreateFormInstance({ orgId, patientRecordId, formKey }) {
  const fn = httpsCallable(functions, 'rmsCreateFormInstance');
  const res = await fn({ orgId, patientRecordId, formKey });
  return res.data; // { instanceId, schemaVersion, context? }
}

/**
 * Load an existing form instance (answers/progress).
 * @param {{ orgId: string, patientRecordId: string, formKey: string, instanceId: string }} args
 * @returns {Promise<{ answers: object, progress: object, schemaVersion: string, lastStepId?: string, context?: object }>}
 */
export async function apiGetFormInstance({ orgId, patientRecordId, formKey, instanceId }) {
  const fn = httpsCallable(functions, 'rmsGetFormInstance');
  const res = await fn({ orgId, patientRecordId, formKey, instanceId });
  return res.data;
}

// The bundle answers in well under a second (TDI-688: every staging call on
// 31 Oct took under 0.31s), so a call still open after this long has stalled
// on the way and the form page offers "Try again" instead of waiting out the
// Firebase client's 70-second default.
export const POST_FORM_BUNDLE_TIMEOUT_MS = 15000;

/**
 * Fetch a post-account form bundle (schema + instance) in one call.
 * Rejects with functions/deadline-exceeded after POST_FORM_BUNDLE_TIMEOUT_MS.
 * @param {{ orgId: string, patientRecordId: string, formKey: string, instanceId?: string }} args
 * @returns {Promise<{ schema: object, version: string, answers: object, sectionProgress?: number[], lastStepId?: string, lastUnanswered?: string, context?: object }>}
 */
export async function apiGetPostFormBundle({ orgId, patientRecordId, formKey, instanceId, createIfMissing = false }) {
  const fn = httpsCallable(functions, 'rmsGetPostFormBundle', { timeout: POST_FORM_BUNDLE_TIMEOUT_MS });
  const res = await fn({ orgId, patientRecordId, formKey, instanceId, createIfMissing });
  return res.data;
}

/**
 * Save a draft patch for a post-account form instance.
 * 
 * @param {{ orgId: string, patientRecordId: string, formKey: string, instanceId: string, patch: object, stepId?: string, sectionProgress?: number[] }} args
 * @param args.sectionProgress - Optional array of percentages (0-100) for each section.
 *   Calculated by the frontend on forward navigation only (not while typing).
 *   When provided, backend stores this value directly for single source of truth.
 *   If not provided (e.g., network retry, old client), backend calculates its own.
 * @returns {Promise<{ ok: boolean, updatedAt: string, progress: { percent: number, sectionProgress: number[] } }>}
 */
export async function apiSaveFormDraft({ orgId, patientRecordId, formKey, instanceId, patch, stepId, context, idempotencyKey, sectionProgress }) {
  const fn = httpsCallable(functions, 'rmsSaveFormDraft');
  const res = await fn({ orgId, patientRecordId, formKey, instanceId, patch, stepId, context, idempotencyKey, sectionProgress });
  return res.data;
}

/**
 * Submit a post-account form instance.
 * @param {{ orgId: string, patientRecordId: string, formKey: string, instanceId: string, idempotencyKey?: string }} args
 * @returns {Promise<{ ok: boolean }>}
 */
export async function apiSubmitForm({ orgId, patientRecordId, formKey, instanceId, idempotencyKey }) {
  const fn = httpsCallable(functions, 'rmsSubmitForm');
  const payload = { orgId, patientRecordId, formKey, instanceId, idempotencyKey };
  // Submit is the critical status flip: a form that never reaches 'submitted' reads as
  // incomplete even at 100%, so a transient blip here can silently strand a completed
  // form as a draft. Retry transient failures before surfacing the error. This is safe
  // to retry — the backend is idempotent on resubmission (idempotencyKey + status guard).
  const TRANSIENT = new Set([
    'unavailable', 'deadline-exceeded', 'internal', 'aborted', 'cancelled', 'unknown',
  ]);
  let lastErr;
  for (let attempt = 0; attempt < 3; attempt += 1) {
    try {
      const res = await fn(payload);
      return res.data;
    } catch (e) {
      lastErr = e;
      const code = String((e && e.code) || '').replace(/^functions\//, '');
      if (!TRANSIENT.has(code)) break; // permission/invalid-argument/etc → fail fast
      await new Promise((resolve) => setTimeout(resolve, 400 * (attempt + 1)));
    }
  }
  throw lastErr;
}

/**
 * List participants for a patient record.
 * @param {{ orgId: string, patientRecordId: string }} args
 * @returns {Promise<{ participants: Array<any> }>}
 */
export async function apiGetReferralParticipants({ orgId, patientRecordId }) {
  const fn = httpsCallable(functions, 'rmsGetReferralParticipants');
  const res = await fn({ orgId, patientRecordId });
  return res.data;
}

/**
 * Edit a parent or carer's details: staff (Org Admin / Support, B13 / RMS-59)
 * and, for a pupil on their own list, the school (TDI-622). Enforced
 * server-side. A changed email supersedes that nomination and invites the
 * new address when the old one had the invitation; a school user must send
 * confirmEmailChange. Once the parent has signed in, staff cannot edit and
 * the school can change everything but the email.
 *
 * @param {{ orgId: string, patientRecordId: string, participantId: string,
 *   updates: { firstName: string, lastName: string, phone?: string,
 *   relationship?: string, email?: string }, confirmEmailChange?: boolean }} args
 * @returns {Promise<{ ok: boolean, participantId: string, emailChanged: boolean }>}
 */
export async function apiUpdateGuardianDetails({
  orgId, patientRecordId, participantId, updates, confirmEmailChange = false,
}) {
  const fn = httpsCallable(functions, 'rmsUpdateGuardianDetails');
  const payload = { orgId, patientRecordId, participantId, updates };
  if (confirmEmailChange) payload.confirmEmailChange = true;
  const res = await fn(payload);
  return res.data;
}

/**
 * Record the clinician's final double-confirmation sign-off on a rendered
 * triage outcome letter (TD-249). After sign-off the letter is final and
 * irreversible; only a signed-off letter can be sent.
 *
 * @param {{ orgId: string, patientRecordId: string, pathway?: string }} args
 * @returns {Promise<{ status: string, signedOffBy: { clinicianId: string, clinicianName: string } }>}
 */
export async function apiSignOffTriageLetter({ orgId, patientRecordId, pathway }) {
  const fn = httpsCallable(functions, 'rmsSignOffTriageLetter');
  const res = await fn({ orgId, patientRecordId, ...(pathway ? { pathway } : {}) });
  return res.data;
}

/**
 * Ask the AI to revise one paragraph of the letter under review (TDI-404
 * "suggest changes", TDI-407 "polish my edits"). The revised text comes back
 * as a proposal and nothing is written: the review page records the
 * clinician's acceptance itself. Only the assigned reviewer may call it.
 *
 * @param {{ orgId: string, patientRecordId: string, pathway?: string, sectionKey: string,
 *   mode: 'suggest_changes'|'polish', instruction?: string, currentText?: string }} args
 * @returns {Promise<{ success: boolean, mode: string, sectionKey: string, proposal: string, unchanged: boolean }>}
 */
export async function apiReviseTriageLetterParagraph({
  orgId, patientRecordId, pathway, sectionKey, mode, instruction, currentText,
}) {
  const fn = httpsCallable(functions, 'rmsReviseTriageLetterParagraph', { timeout: 120000 });
  const res = await fn({
    orgId,
    patientRecordId,
    ...(pathway ? { pathway } : {}),
    sectionKey,
    mode,
    ...(instruction ? { instruction } : {}),
    ...(typeof currentText === 'string' ? { currentText } : {}),
  });
  return res.data;
}

/**
 * Send a rendered triage outcome letter to its service-user recipients (TD-145).
 * Recipients are fixed server-side (RMS-195): the letter goes to every linked
 * service user - parent/carer and school representative. The `recipients`
 * argument is informational only and ignored by the backend. The admin roles
 * send, from the Admin Handover screen only (TDI-387); the leaflets that went
 * with an ADHD letter come back in `leaflets` and are recorded on the record.
 *
 * @param {{ orgId: string, patientRecordId: string, recipients?: string[], pathway?: string }} args
 * @returns {Promise<{ status: string, sharedRoles: string[], recipientCount: number, emailedCount: number, leaflets: Array<{key: string, title: string, url: string|null}> }>}
 */
export async function apiPublishTriageLetter({ orgId, patientRecordId, recipients, pathway }) {
  const fn = httpsCallable(functions, 'rmsPublishTriageLetter');
  const res = await fn({ orgId, patientRecordId, recipients, ...(pathway ? { pathway } : {}) });
  return res.data;
}

/**
 * Resend a guardian invite (idempotent).
 * @param {{ orgId: string, patientRecordId: string, participantId: string }} args
 * @returns {Promise<{ ok: boolean, inviteId?: string, previewUrl?: string }>}
 */
export async function apiResendParticipantInvite({ orgId, patientRecordId, participantId }) {
  const fn = httpsCallable(functions, 'rmsResendParticipantInvite');
  const res = await fn({ orgId, patientRecordId, participantId });
  return res.data;
}

/**
 * Get the status of a guardian nomination by email.
 * 
 * Used by NominationField to determine if the nomination should be locked
 * (guardian has accepted invite) or show a warning (invite pending).
 * 
 * @param {{ orgId: string, patientRecordId: string, email: string }} args
 * @returns {Promise<{
 *   found: boolean,
 *   status: string|null,
 *   participantId: string|null,
 *   isLocked: boolean,
 *   isPending: boolean,
 *   firstName: string|null,
 *   lastName: string|null
 * }>}
 */
export async function apiGetGuardianNominationStatus({ orgId, patientRecordId, email }) {
  const fn = httpsCallable(functions, 'rmsGetGuardianNominationStatus');
  const res = await fn({ orgId, patientRecordId, email });
  return res.data;
}


/**
 * List referrals associated with the current user.
 * @param {{ orgId: string }} args
 * @returns {Promise<{ referrals: Array<{ patientRecordId: string, createdAt?: string }> }>}
 */
export async function apiListUserReferrals({ orgId }) {
  const fn = httpsCallable(functions, 'rmsListUserReferrals');
  const res = await fn({ orgId });
  return res.data;
}

/**
 * Authoritative early postcode eligibility decision (pre-account).
 * @param {{ orgId: string, postcodeRaw: string, postcodeNormalized?: string, prospectId?: string, usedPostcodeType?: 'child_home'|'school'|'gp' }} args
 * @returns {Promise<{ eligible: boolean, reasonCode?: string, normalized?: string, areaVersion?: string }>}
 */
export async function apiCheckPostcodeEligibility({ orgId, postcodeRaw, postcodeNormalized, prospectId, usedPostcodeType = 'child_home' }) {
  const fn = httpsCallable(functions, 'rmsCheckPostcodeEligibility');
  const res = await fn({ orgId, prospectId, postcodeRaw, postcodeNormalized, usedPostcodeType });
  return res.data;
}

/**
 * Create or upsert a Legal Guardian user account and org-scoped profile.
 * @param {{ orgId: string, patientRecordId?: string, firstName: string, lastName: string, email: string, phone?: string }} args
 * @returns {Promise<{ ok: boolean, userId: string, guardianUserId: string }>}
 */
export async function apiUpsertLegalGuardianUser({ orgId, patientRecordId, firstName, lastName, email, phone }) {
  const fn = httpsCallable(functions, 'rmsUpsertLegalGuardianUser');
  const res = await fn({ orgId, patientRecordId, firstName, lastName, email, phone });
  return res.data;
}

/**
 * Submit a post-account referral: validates answers and creates a patient record.
 * Skips invitation/account creation – requires auth.
 * @param {{ orgId: string, prospectId: string, clientToken: string, formKey: string, idempotencyKey?: string }} args
 * @returns {Promise<{ ok: boolean, patientRecordId?: string, idempotent?: boolean }>}
 */
export async function apiSubmitPostAccountReferral({ orgId, prospectId, clientToken, formKey, idempotencyKey }) {
  const fn = httpsCallable(functions, 'rmsSubmitPostAccountReferral');
  const res = await fn({ orgId, prospectId, clientToken, formKey, idempotencyKey });
  return res.data;
}

/**
 * DEV/TESTING ONLY: Batch-generate demo patient records at specified referral stages.
 *
 * Creates realistic patient records with all required metadata for the provider
 * dashboard to display them correctly in each stage's table. Uses Firestore
 * batch writes on the backend for performance.
 *
 * @param {{ orgId: string, stages: Array<{ stageId: string, count: number }> }} args
 * @returns {Promise<{ success: boolean, created: Record<string, number>, totalCreated: number, batchesCommitted: number }>}
 */
export async function apiGenerateDemoReferrals({ orgId, stages }) {
  const fn = httpsCallable(functions, 'rmsGenerateDemoReferrals');
  const res = await fn({ orgId, stages });
  return res.data;
}

/**
 * DEV ONLY: assign an intakeConcern to patient records that lack one, so the
 * per-pathway ADHD / autism triage tabs work for older demo data.
 * @param {{ orgId: string }} args
 * @returns {Promise<{ success: boolean, scanned: number, updated: number }>}
 */
export async function apiBackfillIntakeConcern({ orgId }) {
  const fn = httpsCallable(functions, 'rmsBackfillIntakeConcern');
  const res = await fn({ orgId });
  return res.data;
}

/**
 * Update editable fields on an existing RMS provider profile (RMS Org. Admin only).
 *
 * Primary use is setting/changing a clinician's specialism (ASD / ADHD / both).
 * Server enforces Org. Admin + org isolation, and rejects specialism on non-clinicians.
 *
 * Pass `dryRun: true` to preview a specialism change without writing: the result
 * reports `affectedTriageCount` (in-progress triages that would be released
 * because they fall outside the new specialism — TD-42) so the UI can warn first.
 * A real run returns `unassignedTriageCount` and `specialismClaimRefreshed`.
 *
 * @param {{ orgId: string, providerUserId: string, specialism?: string,
 *           role?: string, registrationBody?: string, registrationNumber?: string,
 *           dryRun?: boolean }} args
 * @returns {Promise<{ ok: boolean, dryRun?: boolean, affectedTriageCount?: number,
 *           affectedPatientIds?: string[], unassignedTriageCount?: number,
 *           specialismClaimRefreshed?: boolean }>}
 */
export async function apiUpdateProviderUser({ orgId, providerUserId, ...fields }) {
  const fn = httpsCallable(functions, 'rmsUpdateProviderUser');
  const res = await fn({ orgId, providerUserId, ...fields });
  return res.data;
}

/**
 * Delete an RMS provider user (RMS Org. Admin only).
 *
 * Server disables the Auth user, removes the rmsProviderUsers profile doc, and
 * best-effort removes the rms_accounts membership doc. Org-isolated; the server
 * rejects deleting your own account.
 *
 * @param {{ orgId: string, providerUserId: string }} args
 * @returns {Promise<{ ok: boolean }>}
 */
export async function apiDeleteProviderUser({ orgId, providerUserId }) {
  const fn = httpsCallable(functions, 'rmsDeleteProviderUser');
  const res = await fn({ orgId, providerUserId });
  return res.data; // { ok }
}

/**
 * Mark a referral Ready for triage (LSC-038/LSC-044). Any RMS provider user
 * (clinician, org admin or support) may make this decision; it is what makes
 * the referral triageable — rmsStartTriage refuses unmarked referrals. When
 * the referral's forms are incomplete a reason is required and recorded.
 *
 * @param {{ orgId: string, patientRecordId: string, formsComplete: boolean, incompleteReason?: string }} args
 * @returns {Promise<{ ok: boolean }>}
 */
export async function apiMarkReadyForTriage({ orgId, patientRecordId, formsComplete, incompleteReason }) {
  const fn = httpsCallable(functions, 'rmsMarkReadyForTriage');
  const res = await fn({
    orgId,
    patientRecordId,
    formsComplete: formsComplete === true,
    ...(incompleteReason ? { incompleteReason } : {}),
  });
  return res.data;
}

/**
 * Record the caller's final "Confirm and submit" on one pathway (TDI-415,
 * per pathway since TDI-651): locks their answers on that pathway only.
 * 'referral' — once both respondents have confirmed, submits the referral
 * (marks it Ready for triage with markedAt as the referral received date,
 * TDI-418). 'sen' — once both have confirmed, the strengths and needs
 * profile is made. Service users only, each for their own role; idempotent
 * on repeat calls.
 *
 * confirmations carries the statements the caller ticked (e.g. the school's
 * consent and nominated parent-or-carer confirmations on a wizard-started
 * referral — Tom King, 22 Oct); the server validates and stores them with
 * the confirmation.
 *
 * @param {{ orgId: string, patientRecordId: string, pathway?: 'referral'|'sen', confirmations?: Record<string, boolean> }} args
 * @returns {Promise<{ ok: boolean, pathway: 'referral'|'sen', confirmed: boolean, alreadyConfirmed: boolean, allConfirmed: boolean, triageReady: boolean }>}
 */
export async function apiConfirmSubmission({ orgId, patientRecordId, pathway, confirmations }) {
  const fn = httpsCallable(functions, 'rmsConfirmSubmission');
  const res = await fn({
    orgId,
    patientRecordId,
    ...(pathway ? { pathway } : {}),
    ...(confirmations ? { confirmations } : {}),
  });
  return res.data;
}

/**
 * Email the safety signposting to the person filling in the form, at the moment
 * they give an answer that indicates risk (RMS-314). Fire-and-forget: the
 * caller shows the same content on screen regardless of whether this resolves,
 * so a mail failure never leaves someone without the information.
 *
 * The instance id lets the server read the answer that was just saved, which is
 * how the provider alert is built — the answer never travels in this request.
 *
 * @param {{ orgId: string, patientRecordId: string, formKey: string, instanceId?: string, questionId: string }} args
 * @returns {Promise<{ ok: boolean, skipped?: boolean }>}
 */
export async function apiSendSafetySignposting({ orgId, patientRecordId, formKey, instanceId, questionId }) {
  const fn = httpsCallable(functions, 'rmsSendSafetySignposting');
  const res = await fn({ orgId, patientRecordId, formKey, instanceId, questionId });
  return res.data;
}

/**
 * The urgent risk alerts sent for a referral (TDI-701), for the patient
 * overview flag and the proforma notes. Clinical team only; read-only.
 *
 * @param {{ orgId: string, patientRecordId: string }} args
 * @returns {Promise<{ alerts: Array<object> }>}
 */
export async function apiListUrgentRiskAlerts({ orgId, patientRecordId }) {
  const fn = httpsCallable(functions, 'rmsListUrgentRiskAlerts', { timeout: 30000 });
  const res = await fn({ orgId, patientRecordId });
  return res.data;
}

/**
 * Generate one referral document as a PDF (DOCX fallback) for transfer into
 * EMIS/EIS: all of one respondent's questionnaires in one document, one
 * respondent's answers to a single form (SNAP-IV), or one pathway's triage
 * decision once that pathway's triage is complete. Never the HFP. RMS staff
 * only; the server returns a 10-minute signed download URL.
 *
 * @param {{ orgId: string, patientRecordId: string, document: 'form'|'respondentForms'|'triageDecision', instanceId?: string, role?: 'school_user'|'guardian', pathway?: string|null }} args
 * @returns {Promise<{ status: string, downloadUrl: string, filename: string, fileFormat: string, expiresAt: string }>}
 */
export async function apiExportReferralDocument({ orgId, patientRecordId, document, instanceId, role, pathway }) {
  const fn = httpsCallable(functions, 'rmsExportReferralDocument', { timeout: 300000 });
  const res = await fn({ orgId, patientRecordId, document, instanceId, role, pathway });
  return res.data;
}

/**
 * Start triage on a patient — assigns the calling clinician and locks it (TD-28/29).
 * Server rejects (failed-precondition) if already in triage by another clinician.
 *
 * @param {{ orgId: string, patientRecordId: string }} args
 * @returns {Promise<{ ok: boolean, assignedClinicianId?: string, assignedClinicianName?: string }>}
 */
export async function apiStartTriage({ orgId, patientRecordId, pathway }) {
  const fn = httpsCallable(functions, 'rmsStartTriage');
  // `pathway` (adhd|asd) is required for combined referrals (triaged per pathway);
  // ignored server-side for single-concern referrals.
  const res = await fn({ orgId, patientRecordId, ...(pathway ? { pathway } : {}) });
  return res.data;
}

/**
 * Reopen the caller's own triage decision so they can change it, until the
 * letter is signed off (TDI-695). The server keeps the decision and any letter
 * draft for audit and returns the pathway to "in review" with this clinician.
 */
export async function apiReopenTriageDecision({ orgId, patientRecordId, pathway }) {
  const fn = httpsCallable(functions, 'rmsReopenTriageDecision');
  const res = await fn({ orgId, patientRecordId, ...(pathway ? { pathway } : {}) });
  return res.data;
}

/**
 * Add or remove one of the caller's own evidence tags on a referral answer:
 * the clinician's note that this answer is the evidence for a given criterion,
 * made while reading the proforma and carried into the decision wizard.
 *
 * Tags are stored per pathway and are private to the clinician who made them,
 * so an autism second opinion still starts from that reviewer's own reading.
 *
 * @param {{ orgId: string, patientRecordId: string, pathway?: string,
 *           action: 'add'|'remove', tag?: object, tagId?: string }} args
 * @returns {Promise<{ ok: boolean, tags: Array }>} the caller's tags after the change
 */
export async function apiUpdateEvidenceTags({ orgId, patientRecordId, pathway, action, tag, tagId }) {
  const fn = httpsCallable(functions, 'rmsUpdateEvidenceTags');
  const res = await fn({
    orgId,
    patientRecordId,
    action,
    ...(pathway ? { pathway } : {}),
    ...(tag ? { tag } : {}),
    ...(tagId ? { tagId } : {}),
  });
  return res.data;
}

/**
 * Record or clear one "Added to EMIS" tick on a referral's admin handover
 * (TDI-386): the admin team's saved progress while filing documents into
 * EMIS, stored on the record (per pathway for combined referrals) so it
 * survives leaving the screen. Support and org admins only, enforced
 * server-side; idempotent either way.
 *
 * @param {{ orgId: string, patientRecordId: string, docKey: string,
 *           ticked?: boolean, pathway?: string }} args
 * @returns {Promise<{ ok: boolean }>}
 */
export async function apiSetAdminHandoverTick({ orgId, patientRecordId, docKey, ticked = true, pathway }) {
  const fn = httpsCallable(functions, 'rmsSetAdminHandoverTick');
  const res = await fn({
    orgId,
    patientRecordId,
    docKey,
    ticked,
    ...(pathway ? { pathway } : {}),
  });
  return res.data;
}

/**
 * Record that admin have finished the EMIS handover for a referral (TDI-391):
 * what moves the child out of Admin Handover to Accepted or Not accepted.
 * Per pathway for combined referrals. The server refuses it before the letter
 * is sent and on a withdrawn referral; a repeat press returns alreadyComplete
 * without writing.
 *
 * @param {{ orgId: string, patientRecordId: string, pathway?: string }} args
 * @returns {Promise<{ ok: boolean, alreadyComplete: boolean }>}
 */
export async function apiCompleteAdminHandover({ orgId, patientRecordId, pathway }) {
  const fn = httpsCallable(functions, 'rmsCompleteAdminHandover');
  const res = await fn({
    orgId,
    patientRecordId,
    ...(pathway ? { pathway } : {}),
  });
  return res.data;
}

/**
 * Release a patient from triage (TD-28). Allowed for the assigned clinician
 * (self) or an RMS Org. Admin / RMS Support (force-unassign); enforced server-side.
 * Releasing another clinician's triage requires a `reason` — the server emails
 * it to the affected clinician (LSC-033).
 *
 * @param {{ orgId: string, patientRecordId: string, pathway?: string|null, reason?: string }} args
 * @returns {Promise<{ ok: boolean }>}
 */
export async function apiUnassignTriage({ orgId, patientRecordId, pathway = null, reason = '' }) {
  const fn = httpsCallable(functions, 'rmsUnassignTriage');
  // `pathway` (adhd|asd) is required for combined referrals (locked per pathway);
  // ignored server-side for single-concern referrals.
  const trimmedReason = (reason || '').trim();
  const res = await fn({
    orgId,
    patientRecordId,
    ...(pathway ? { pathway } : {}),
    ...(trimmedReason ? { reason: trimmedReason } : {}),
  });
  return res.data;
}

// ─────────────────────────────────────────────────────────────────────────────
// Harbour SENCo portal (SEN & ND Access Portal, TDI-53)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Create a pupil record (no pathway active yet). School users only.
 * The parent or carer is stored as a nominated participant; no email is sent.
 *
 * @param {{
 *   orgId: string,
 *   child: { firstName: string, middleName?: string, lastName: string, dob: string },
 *   address?: { street?: string, city?: string, postcode?: string },
 *   parent: { firstName: string, lastName: string, email: string, phone?: string, relationship?: string },
 *   confirmDuplicate?: boolean,
 *   idempotencyKey?: string,
 * }} args
 * @returns {Promise<{ ok: boolean, patientRecordId: string, idempotent?: boolean }>}
 */
export async function apiHarbourAddPupil({ orgId, child, address, parent, confirmDuplicate, idempotencyKey }) {
  const fn = httpsCallable(functions, 'rmsHarbourAddPupil');
  const res = await fn({ orgId, child, address, parent, confirmDuplicate, idempotencyKey });
  return res.data;
}

/**
 * Place a pupil on the SEN pathway (stage Assess) after confirming consent.
 * @param {{ orgId: string, patientRecordId: string, consentConfirmed: boolean }} args
 * @returns {Promise<{ ok: boolean, alreadyActive?: boolean }>}
 */
export async function apiHarbourStartSenPathway({ orgId, patientRecordId, consentConfirmed, consentVersion }) {
  const fn = httpsCallable(functions, 'rmsHarbourStartSenPathway');
  // consentVersion names the wording this screen showed (TDI-480).
  const res = await fn({ orgId, patientRecordId, consentConfirmed, consentVersion });
  return res.data;
}

/**
 * Stop a pupil's SEN pathway, recording the reason.
 * @param {{ orgId: string, patientRecordId: string, reason: 'consent_withdrawn'|'other' }} args
 * @returns {Promise<{ ok: boolean }>}
 */
export async function apiHarbourStopSenPathway({ orgId, patientRecordId, reason }) {
  const fn = httpsCallable(functions, 'rmsHarbourStopSenPathway');
  const res = await fn({ orgId, patientRecordId, reason });
  return res.data;
}

/**
 * Put a teacher on a pupil's record (TDI-616): one chosen from the school's
 * list (schoolTeacherId), or a new teacher, who is added to the school's list
 * first. Sends no email. School users only.
 *
 * A new teacher whose email is already on the school's list comes back as
 * { ok: false, code: 'ALREADY_ON_SCHOOL_LIST', teacher } instead.
 * @param {{ orgId: string, patientRecordId: string, schoolTeacherId?: string,
 *           firstName?: string, lastName?: string, email?: string,
 *           jobRole?: string, phone?: string|null }} args
 * @returns {Promise<{ ok: boolean, code?: string, participantId?: string,
 *           schoolTeacherId?: string, teacher?: object, alreadyListed?: boolean }>}
 */
export async function apiHarbourAddTeacher({
  orgId, patientRecordId, schoolTeacherId, firstName, lastName, email, jobRole, phone,
}) {
  const fn = httpsCallable(functions, 'rmsHarbourAddTeacher');
  const payload = schoolTeacherId
    ? { orgId, patientRecordId, schoolTeacherId }
    : {
        orgId,
        patientRecordId,
        firstName,
        lastName,
        email,
        jobRole,
        ...(phone ? { phone } : {}),
      };
  const res = await fn(payload);
  return res.data;
}

/**
 * The caller's school's teacher list (TDI-616), with former teachers when
 * asked. School users only; the school is worked out on the server.
 * @param {{ orgId: string, patientRecordId: string, includeFormer?: boolean }} args
 * @returns {Promise<{ ok: boolean, schoolName: string, teachers: Array<object> }>}
 */
export async function apiHarbourListSchoolTeachers({ orgId, patientRecordId, includeFormer = false }) {
  const fn = httpsCallable(functions, 'rmsHarbourListSchoolTeachers');
  const res = await fn({ orgId, patientRecordId, includeFormer });
  return res.data;
}

/**
 * Allocate one school form to a listed teacher, or take the allocation back
 * (TDI-489). School users only; the first allocation emails the teacher.
 * @param {{ orgId: string, patientRecordId: string, participantId: string,
 *           formKey: string, allocated: boolean }} args
 */
export async function apiHarbourSetTeacherAllocation({ orgId, patientRecordId, participantId, formKey, allocated }) {
  const fn = httpsCallable(functions, 'rmsHarbourSetTeacherAllocation');
  const res = await fn({ orgId, patientRecordId, participantId, formKey, allocated });
  return res.data;
}

/**
 * Remove a listed teacher from a pupil's record (TDI-489). School users only;
 * their access ends at once and the history is kept.
 * @param {{ orgId: string, patientRecordId: string, participantId: string }} args
 */
export async function apiHarbourRemoveTeacher({ orgId, patientRecordId, participantId }) {
  const fn = httpsCallable(functions, 'rmsHarbourRemoveTeacher');
  const res = await fn({ orgId, patientRecordId, participantId });
  return res.data;
}

/**
 * Save the whole set of school forms one teacher fills in for one pupil,
 * and email them once listing the forms given (TDI-618). A form held by
 * another teacher comes back as { ok: false, code: 'HELD_BY_OTHER', heldBy }
 * unless confirmMoves is true. School users only.
 * @param {{ orgId: string, patientRecordId: string, participantId: string,
 *           formKeys: string[], confirmMoves?: boolean }} args
 * @returns {Promise<{ ok: boolean, code?: string, heldBy?: Array<object>,
 *           allocations?: string[], added?: string[], removed?: string[],
 *           moved?: string[], emailed?: boolean }>}
 */
export async function apiHarbourSaveTeacherForms({
  orgId, patientRecordId, participantId, formKeys, confirmMoves = false,
}) {
  const fn = httpsCallable(functions, 'rmsHarbourSaveTeacherForms');
  const res = await fn({ orgId, patientRecordId, participantId, formKeys, confirmMoves });
  return res.data;
}

/**
 * "Change teacher" (TDI-617): save a teacher's details on the school's
 * list. A changed email needs confirmEmailChange: true; a new address
 * already on the list comes back as { ok: false, code: 'ALREADY_ON_SCHOOL_LIST' }.
 * @param {{ orgId: string, patientRecordId: string, schoolTeacherId: string,
 *           firstName: string, lastName: string, email: string, jobRole: string,
 *           phone?: string|null, confirmEmailChange?: boolean }} args
 * @returns {Promise<{ ok: boolean, code?: string, teacher?: object,
 *           changed?: string[], emailChanged?: boolean }>}
 */
export async function apiHarbourUpdateSchoolTeacher({
  orgId, patientRecordId, schoolTeacherId, firstName, lastName, email, jobRole, phone,
  confirmEmailChange = false,
}) {
  const fn = httpsCallable(functions, 'rmsHarbourUpdateSchoolTeacher');
  const res = await fn({
    orgId, patientRecordId, schoolTeacherId, firstName, lastName, email, jobRole,
    phone: phone || null, confirmEmailChange,
  });
  return res.data;
}

/**
 * Mark a teacher as no longer at the school ('left') or bring them back
 * ('active') (TDI-617). School users only.
 * @param {{ orgId: string, patientRecordId?: string, schoolTeacherId: string,
 *           status: 'left'|'active' }} args
 * @returns {Promise<{ ok: boolean, teacher: object, unfinishedCount: number }>}
 */
export async function apiHarbourSetSchoolTeacherStatus({ orgId, patientRecordId, schoolTeacherId, status }) {
  const fn = httpsCallable(functions, 'rmsHarbourSetSchoolTeacherStatus');
  const res = await fn({ orgId, patientRecordId, schoolTeacherId, status });
  return res.data;
}

/**
 * The Teachers list on the provider Schools page (TDI-621): every school's
 * teacher records in the organisation. Org Admins and Support Users; only
 * Org Admins get canManage. Changes go through apiHarbourUpdateSchoolTeacher
 * and apiHarbourSetSchoolTeacherStatus (no patientRecordId), which accept an
 * Org Admin for any school.
 *
 * @returns {Promise<{ ok: boolean, canManage: boolean, schools: string[],
 *   teachers: Array<{ schoolTeacherId: string, firstName: string,
 *   lastName: string, email: string, jobRole: string|null,
 *   phone: string|null, status: 'active'|'left', schoolName: string|null,
 *   pupilCount: number, providerStatus: 'active'|'email_sent'|'left',
 *   lastSignedInAt: string|null, canResend: boolean }> }>}
 */
export async function apiProviderListSchoolTeachers({ orgId }) {
  const fn = httpsCallable(functions, 'rmsProviderListSchoolTeachers');
  const res = await fn({ orgId });
  return res.data;
}

/**
 * "Send the email again" from the provider Schools page (TDI-621): the
 * first email again with a fresh link, before the teacher sets a password.
 * Org Admins only.
 */
export async function apiProviderResendSchoolTeacherEmail({ orgId, schoolTeacherId }) {
  const fn = httpsCallable(functions, 'rmsProviderResendSchoolTeacherEmail');
  const res = await fn({ orgId, schoolTeacherId });
  return res.data;
}

/**
 * The signed-in teacher's own task list (TDI-489): children and allocated
 * school forms with progress. First call links the account.
 * @param {{ orgId: string }} args
 */
export async function apiListTeacherTasks({ orgId }) {
  const fn = httpsCallable(functions, 'rmsListTeacherTasks');
  const res = await fn({ orgId });
  return res.data;
}

/**
 * Record the teacher's acceptance of the school-staff statement (TDI-358).
 * @param {{ orgId: string }} args
 */
export async function apiAcceptTeacherStatement({ orgId }) {
  const fn = httpsCallable(functions, 'rmsAcceptTeacherStatement');
  const res = await fn({ orgId });
  return res.data;
}

/**
 * Record one Plan-stage action on a pupil's SEN pathway (family meeting
 * scheduled, or SEND support plan created). School users only; the ticks only
 * exist once the profile report does; idempotent per action.
 * @param {{ orgId: string, patientRecordId: string, action: 'meetingScheduled'|'planCreated', ticked?: boolean }} args
 *   ticked=false clears a recorded step.
 * @returns {Promise<{ ok: boolean, alreadyRecorded?: boolean, cleared?: boolean }>}
 */
export async function apiHarbourSetSenPlanAction({ orgId, patientRecordId, action, ticked = true }) {
  const fn = httpsCallable(functions, 'rmsHarbourSetSenPlanAction');
  const res = await fn({ orgId, patientRecordId, action, ticked });
  return res.data;
}

/**
 * Record the signed-in guardian's own consent decision for one pathway on
 * their child's record (family portal). 'disagree' and 'withdraw' also stop
 * that pathway (stopReason no_consent / consent_withdrawn).
 * @param {{ orgId: string, patientRecordId: string, pathway: 'sen'|'referral', decision: 'agree'|'disagree'|'withdraw' }} args
 * @returns {Promise<{ ok: boolean }>}
 */
export async function apiFamilyPathwayConsent({
  orgId, patientRecordId, pathway, decision, consentVersion, profileSharing,
}) {
  const fn = httpsCallable(functions, 'rmsFamilyPathwayConsent');
  // consentVersion names the wording this screen showed (TDI-480).
  // profileSharing ('yes' | 'no' | null) travels with a referral agree
  // (TDI-482); the server records nothing chosen as No.
  const payload = { orgId, patientRecordId, pathway, decision, consentVersion };
  if (profileSharing !== undefined) payload.profileSharing = profileSharing;
  const res = await fn(payload);
  return res.data;
}

/**
 * Change whether the hospital team may see the child's strengths and needs
 * profile (TDI-482), until the referral has its outcome.
 * @param {{ orgId: string, patientRecordId: string, answer: 'yes'|'no' }} args
 */
export async function apiFamilySetProfileSharing({ orgId, patientRecordId, answer }) {
  const fn = httpsCallable(functions, 'rmsFamilySetProfileSharing');
  const res = await fn({ orgId, patientRecordId, answer });
  return res.data;
}

/**
 * Record that a parent or carer told the service they are withdrawing
 * consent for the referral (TDI-476): the date they said it, how they said
 * it ('email' | 'phone' | 'other'), and a note, which the server requires
 * for 'other'. Stops the referral through the same engine as a family
 * withdrawal. Any provider user in the org.
 */
export async function apiRecordConsentWithdrawal({ orgId, patientRecordId, withdrawnOn, method, note }) {
  const fn = httpsCallable(functions, 'rmsRecordConsentWithdrawal');
  const res = await fn({ orgId, patientRecordId, withdrawnOn, method, note });
  return res.data;
}

/**
 * Invite the pupil's parent or carer to complete their side of the forms,
 * optionally upserting fresh contact details first.
 * @param {{ orgId: string, patientRecordId: string, parent?: object, participantId?: string }} args
 *   participantId names the parent or carer on the record the link goes to.
 * @returns {Promise<{ ok: boolean, inviteId?: string, alreadyLinked?: boolean, notified?: boolean }>}
 *   notified: a linked parent was emailed (no link) that a pathway is waiting
 *   for their permission (TDI-791) — an invite cannot reach them.
 */
export async function apiHarbourInviteFamily({ orgId, patientRecordId, parent, participantId }) {
  const fn = httpsCallable(functions, 'rmsHarbourInviteFamily');
  const res = await fn({ orgId, patientRecordId, parent, participantId });
  return res.data;
}

/**
 * Add a pupil to the referral pathway: record consent, set the intake concern
 * and invite the nominated informant.
 * @param {{
 *   orgId: string,
 *   patientRecordId: string,
 *   consentAnswer: 'yes'|'no'|'not_sure',
 *   referralFor: 'adhd'|'asd'|'both',
 *   informant: { participantId?: string, firstName?: string, lastName?: string, email?: string, phone?: string, relationship?: string },
 *   guardianToldToExpectEmail: 'yes'|'no',
 * }} args
 * @returns {Promise<{ ok: boolean, inviteId?: string }>}
 */
export async function apiHarbourStartReferral({
  orgId, patientRecordId, consentAnswer, referralFor, gpPractice, informant, guardianToldToExpectEmail,
  consentVersion,
}) {
  const fn = httpsCallable(functions, 'rmsHarbourStartReferral');
  // consentVersion names the wording this screen showed (TDI-480).
  const res = await fn({
    orgId, patientRecordId, consentAnswer, referralFor, gpPractice, informant, guardianToldToExpectEmail,
    consentVersion,
  });
  return res.data;
}

/**
 * Update a Harbour pupil's details (no active referral only).
 * @param {{ orgId: string, patientRecordId: string, child?: object, address?: object }} args
 * @returns {Promise<{ ok: boolean, unchanged?: boolean }>}
 */
export async function apiHarbourUpdatePupil({ orgId, patientRecordId, child, address }) {
  const fn = httpsCallable(functions, 'rmsHarbourUpdatePupil');
  const res = await fn({ orgId, patientRecordId, child, address });
  return res.data;
}

/**
 * Remove a pending (never-linked) parent or carer from a pupil record.
 * @param {{ orgId: string, patientRecordId: string, participantId: string }} args
 * @returns {Promise<{ ok: boolean }>}
 */
export async function apiHarbourRemoveParent({ orgId, patientRecordId, participantId }) {
  const fn = httpsCallable(functions, 'rmsHarbourRemoveParent');
  const res = await fn({ orgId, patientRecordId, participantId });
  return res.data;
}

/**
 * One record's HFP v4 report as one view (TDI-522): the family or school
 * view for a service user, the clinician view for a provider.
 * @param {{ orgId: string, patientRecordId: string, view?: 'family'|'school' }} args
 * @returns {Promise<{ status: string, view?: string, report?: object, child?: object, reason?: string }>}
 */
export async function apiGetHfpV4Report({ orgId, patientRecordId, view }) {
  const fn = httpsCallable(functions, 'rmsGetHfpV4Report', { timeout: 60000 });
  const res = await fn({ orgId, patientRecordId, ...(view ? { view } : {}) });
  return res.data;
}

// ── HFP v4 forms (TDI-525) ────────────────────────────────────────────────────

/**
 * The respondent's v4 form: allocation, the form bundle (screens, follow-ons
 * and the show-and-hide predicates) and their saved answers to resume from.
 * @returns {Promise<{ allocated: boolean, held?: string, message?: string, band?: string,
 *   bundle?: object, draft?: { answers: object, started: boolean, submitted: boolean } }>}
 */
export async function apiGetHfpV4Form({ orgId, patientRecordId }) {
  const fn = httpsCallable(functions, 'rmsGetHfpV4Form', { timeout: 60000 });
  const res = await fn({ orgId, patientRecordId });
  return res.data;
}

/** Save answers in the draft-save shape; `progress` mirrors the portal count. */
export async function apiSaveHfpV4Draft({ orgId, patientRecordId, answers, progress }) {
  const fn = httpsCallable(functions, 'rmsSaveHfpV4Draft');
  const res = await fn({ orgId, patientRecordId, answers, ...(progress ? { progress } : {}) });
  return res.data;
}

/** Screen visits: { screen_id, position, opened_at, answered_at?, answer_id?, device_class, event }. */
export async function apiRecordHfpV4ScreenEvents({ orgId, patientRecordId, events }) {
  const fn = httpsCallable(functions, 'rmsRecordHfpV4ScreenEvents');
  const res = await fn({ orgId, patientRecordId, events });
  return res.data;
}

export async function apiSubmitHfpV4Form({ orgId, patientRecordId }) {
  const fn = httpsCallable(functions, 'rmsSubmitHfpV4Form', { timeout: 60000 });
  const res = await fn({ orgId, patientRecordId });
  return res.data;
}

/*
 * A referral's uploaded documents (TDI-602). Read-only: the documents belong
 * to the school or the family who attached them (TDI-56).
 */
export async function apiListReferralDocuments({ orgId, patientRecordId }) {
  const fn = httpsCallable(functions, 'rmsListReferralDocuments', { timeout: 30000 });
  const res = await fn({ orgId, patientRecordId });
  return res.data;
}

/**
 * A short-lived signed URL for one document. `disposition` is 'inline' to
 * view (any provider) or 'attachment' to download, which the server allows
 * the admin roles only (FR8).
 */
export async function apiFetchReferralDocument({ orgId, patientRecordId, documentId, disposition = 'inline' }) {
  const fn = httpsCallable(functions, 'rmsFetchReferralDocument', { timeout: 30000 });
  const res = await fn({ orgId, patientRecordId, documentId, disposition });
  return res.data;
}

/*
 * The service user's own side of a referral's documents (TDI-577, TDI-578,
 * TDI-579). A school user sees the school's documents; a parent or carer
 * sees only their own, and their cap. Neither sees the other side's.
 * Returns { documents, sent, onReferralPathway, canAdd, addRefusal,
 * documentTypeGroups, maxFileBytes, cap?, remaining? }.
 */
export async function apiListMyReferralDocuments({ orgId, patientRecordId }) {
  const fn = httpsCallable(functions, 'rmsListMyReferralDocuments', { timeout: 30000 });
  const res = await fn({ orgId, patientRecordId });
  return res.data;
}

/**
 * Add one labelled PDF or Word file to a referral before it is sent. The
 * file travels as base64 in the call (20 MB at most); every rule is checked
 * on the server. Returns { document }.
 */
export async function apiAddReferralDocument({
  orgId, patientRecordId, fileName, fileBase64, description, documentType, dateCovered,
}) {
  const fn = httpsCallable(functions, 'rmsAddReferralDocument', { timeout: 300000 });
  const res = await fn({ orgId, patientRecordId, fileName, fileBase64, description, documentType, dateCovered });
  return res.data;
}

/** Remove one of the caller's own documents, before the referral is sent. */
export async function apiRemoveReferralDocument({ orgId, patientRecordId, documentId }) {
  const fn = httpsCallable(functions, 'rmsRemoveReferralDocument', { timeout: 30000 });
  const res = await fn({ orgId, patientRecordId, documentId });
  return res.data;
}

/**
 * Record that the signed-in staff user has acknowledged the confidentiality
 * warning for this patient record (TDI-609), so it does not show again on
 * their later downloads for the same patient, here or on another device.
 */
export async function apiRecordConfidentialWarningSeen({ orgId, patientRecordId }) {
  const fn = httpsCallable(functions, 'rmsRecordConfidentialWarningSeen', { timeout: 15000 });
  const res = await fn({ orgId, patientRecordId });
  return res.data;
}

/**
 * SENCO account management on the School users page (TDI-772): Org Admins
 * resend a set-up email, deactivate an account and bring it back, without
 * ThinkDivergent. The server enforces the Org Admin gate and writes an audit
 * event on the profile with the admin as actor.
 */
export async function apiResendSencoSetupEmail({ orgId, schoolUserId }) {
  const fn = httpsCallable(functions, 'rmsResendSencoSetupEmail', { timeout: 30000 });
  const res = await fn({ orgId, schoolUserId });
  return res.data;
}

export async function apiDeactivateSchoolUser({ orgId, schoolUserId }) {
  const fn = httpsCallable(functions, 'rmsDeactivateSchoolUser', { timeout: 30000 });
  const res = await fn({ orgId, schoolUserId });
  return res.data;
}

export async function apiReactivateSchoolUser({ orgId, schoolUserId }) {
  const fn = httpsCallable(functions, 'rmsReactivateSchoolUser', { timeout: 30000 });
  const res = await fn({ orgId, schoolUserId });
  return res.data;
}
